import React, { useCallback, useEffect, useMemo, useState } from "react";
import EpsfPageBreadcrumb from "../EpsfPageBreadcrumb";
import ReportTable from "../../../common/ReportTable/ReportTable";
import Dropdown from "../../../common/dropDown";
import SegmentDatePicker from "../../../common/aqPickers/SegmentDatePicker";
import SegmentTimePicker from "../../../common/aqPickers/SegmentTimePicker";
import InfoAlert from "../../../common/InfoAlert";
import useInfoAlert from "../../../../hooks/useInfoAlert";
import { openAuthModal } from "../../../common/authmodal/authmodalservice";
import { openESignatureConfirmModal } from "../../../common/esignature/esignatureConfirmService";
import { getUserData } from "../../../../services/apiService";
import { EpsfAccessGate, useEpsfAccess } from "../epsfAccess";
import { renderReceiptStatusBadge } from "../epsfBadges";
import EpsfFormFillModal from "../EpsfFormFillModal";
import {
  batchOptionLabel,
  findOption,
  formatImpQtyWithTracking,
  getImpTxnRowKey,
  IMP_DISPENSE_DEFAULT_COLUMNS,
  IMP_DISPENSE_FIELD_OPTIONS,
  renderImpMovementCell,
  sanitizeEpsfDisplayLabel,
  toApiDateTimeString,
  useEpsfLedger,
  useEpsfStudySite,
} from "../epsfLedgerUi";
import {
  checkFormActionBlockers,
  confirmDispenseRequest,
  createDispenseRequest,
  fetchFormWorkItems,
  fetchImpKits,
  fetchStorageUnits,
  fetchStudyParticipants,
  formBlockersNeedRequest,
  formBlockMessageForAction,
  linkFormInstance,
  postImpDispense,
} from "../epsfService";
import { createInstance } from "../../esource/esourceService";
import { matchStorageUnitIdFromBatch, matchStorageRackIdFromBatch } from "./inventory/inventoryDashboardUtils";
import { formatStorageTempRange } from "./impProductExcel";
import ImpReturns from "./ImpReturns";
import { runEmergencyUnblind } from "./emergencyUnblind";
import "../EpsfPages.scss";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function nowTimeHHmm() {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function newClientRequestId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `disp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isDispenseFormTrigger(trigger) {
  const t = String(trigger || "").toUpperCase();
  return t === "ONDISPENSE" || t === "DISPENSE_POSTED";
}

function workItemStatus(row) {
  return String(
    row?.WorkItemStatus || row?.workItemStatus || row?.Status || row?.status || "",
  ).toUpperCase();
}

function workItemType(row) {
  return String(row?.WorkType || row?.workType || "COMPLETE").toUpperCase();
}

function workflowStatus(row) {
  return String(row?.WorkflowStatus || row?.workflowStatus || "").toUpperCase();
}

function summarizeDispenseFormItems(items = []) {
  const list = Array.isArray(items) ? items : [];
  const openFill = list.filter((row) => {
    const st = workItemStatus(row);
    const wt = workItemType(row);
    return st === "OPEN" && (wt === "COMPLETE" || wt === "CORRECT");
  });
  if (openFill.length) {
    return { phase: "fill", items: openFill, label: "Fill form" };
  }
  const openReview = list.filter((row) => {
    const st = workItemStatus(row);
    const wt = workItemType(row);
    return st === "OPEN" && wt === "REVIEW";
  });
  if (openReview.length) {
    return { phase: "approve", items: openReview, label: "Approval pending" };
  }
  const locked = list.filter((row) => {
    const wf = workflowStatus(row);
    const occ = String(
      row?.OccurrenceStatus || row?.occurrenceStatus || "",
    ).toUpperCase();
    return (
      wf === "APPROVED" ||
      wf === "COMPLETED" ||
      occ === "COMPLETE" ||
      occ === "COMPLETED"
    );
  });
  if (locked.length) {
    const wf = workflowStatus(locked[0]);
    return {
      phase: "locked",
      items: [locked[0]],
      label: wf === "APPROVED" ? "Approved" : "Completed",
    };
  }
  return { phase: "none", items: [], label: "" };
}

function isDispenseRequestedRow(row) {
  return (
    String(row?.ReceiptStatus || row?.receiptStatus || "").trim() ===
    "DispenseRequested"
  );
}

const emptyForm = () => ({
  participantId: "",
  impBatchId: "",
  storageUnitId: "",
  storageRackId: "",
  txnDate: new Date(),
  txnTime: nowTimeHHmm(),
  referenceNo: "",
  selectedKitIds: [],
});

export default function ImpDispense({
  overlay = false,
  preferImpProductId = null,
  preferImpBatchId = null,
  onOverlayClose = null,
  onOverlaySuccess = null,
} = {}) {
  const { loading, canView, moduleAccess, canDispense, canReturn, canEmergencyUnblind } =
    useEpsfAccess();
  const allow = canView && moduleAccess;
  const ctx = useEpsfStudySite();
  const user = useMemo(() => getUserData() || {}, []);
  const staffName = [user.FirstName || user.firstName, user.LastName || user.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const { balances, txns, error, setError, refresh, loading: listLoading } =
    useEpsfLedger(
    ctx.studyId,
    ctx.siteId,
    "Dispense",
  );

  const preferProductKey = String(preferImpProductId || "").toLowerCase();
  const preferBatchId = String(preferImpBatchId || "").trim();

  const [showModal, setShowModal] = useState(false);
  const [returnOverlayTxnId, setReturnOverlayTxnId] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [formBlocked, setFormBlocked] = useState(false);
  const [formBlockMessage, setFormBlockMessage] = useState("");
  const [formGateBusy, setFormGateBusy] = useState(false);
  const [needsRequest, setNeedsRequest] = useState(false);
  const [requestBusy, setRequestBusy] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [kits, setKits] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [units, setUnits] = useState([]);
  const [racks, setRacks] = useState([]);
  const [clientRequestId, setClientRequestId] = useState(newClientRequestId);
  const [formDueByTxn, setFormDueByTxn] = useState({});
  const [formDueTick, setFormDueTick] = useState(0);
  const [formFillBusy, setFormFillBusy] = useState(false);
  const [formFillModal, setFormFillModal] = useState(null);
  const { alertState, showAlert, hideAlert } = useInfoAlert();

  const refreshFormDues = useCallback(() => {
    setFormDueTick((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ctx.studyId || !ctx.siteId) {
        setFormDueByTxn({});
        return;
      }
      try {
        const work = await fetchFormWorkItems(ctx.studyId, ctx.siteId, "*");
        if (cancelled) return;
        const map = {};
        (work.rows || []).forEach((row) => {
          const txnId = String(
            row.LinkedTxnId || row.linkedTxnId || "",
          ).toLowerCase();
          if (!txnId || !isDispenseFormTrigger(row.TriggerCode || row.triggerCode)) {
            return;
          }
          if (!map[txnId]) map[txnId] = [];
          map[txnId].push(row);
        });
        setFormDueByTxn(map);
      } catch {
        if (!cancelled) setFormDueByTxn({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ctx.studyId, ctx.siteId, txns, formDueTick]);

  const dispenseRows = useMemo(
    () =>
      (txns || []).map((row) => {
        const txnId = String(row.ImpTxnId || row.impTxnId || "").toLowerCase();
        const dues = formDueByTxn[txnId] || [];
        const summary = summarizeDispenseFormItems(dues);
        const titles = summary.items.map(
          (d) =>
            d.Title ||
            d.title ||
            d.TemplateName ||
            d.templateName ||
            summary.label ||
            "Form",
        );
        return {
          ...row,
          FormDue: titles.length ? titles.join("; ") : "",
          FormDueCount: summary.items.length,
          FormDueItems: summary.items,
          FormDuePhase: summary.phase,
          FormDueLabel: summary.label,
        };
      }),
    [txns, formDueByTxn],
  );

  const participantOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    (participants || []).forEach((p) => {
      const id = String(p.ParticipantId || p.participantId || "").trim();
      if (!id || seen.has(id.toLowerCase())) return;
      seen.add(id.toLowerCase());

      const fromApi = sanitizeEpsfDisplayLabel(
        p.ParticipantLabel || p.participantLabel,
      );
      const volunteerNo = sanitizeEpsfDisplayLabel(
        p.VolunteerNo || p.volunteerNo,
      );
      const screening = sanitizeEpsfDisplayLabel(
        p.ScreeningId || p.screeningId,
      );
      // Dispense picker: volunteer / screening ID only (no full names).
      const idBadge = volunteerNo || screening;
      const apiIdOnly = fromApi
        ? String(fromApi).split(/\s[-–—]\s/)[0].trim()
        : "";
      const label = idBadge || apiIdOnly || id;
      if (!label) return;

      opts.push({ value: id, label });
    });

    opts.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );

    return [{ value: "", label: "Select participant" }, ...opts];
  }, [participants]);

  const batchOptions = useMemo(
    () =>
      balances
        .filter(
          (b) =>
            Number(
              b.AvailableOnHand ?? b.availableOnHand ?? b.OnHand ?? b.onHand ?? 0,
            ) > 0,
        )
        .filter((b) => !(b.IsRecalled || b.isRecalled))
        .filter((b) => {
          const st = String(b.Status || b.status || "Active");
          return !["Quarantined", "Hold", "Recalled"].includes(st);
        })
        .filter((b) => {
          const tracking = String(
            b.InventoryTrackingMethod || b.inventoryTrackingMethod || "",
          );
          return tracking === "IndividualKit";
        })
        .filter((b) => {
          if (!preferProductKey) return true;
          return (
            String(b.ImpProductId || b.impProductId || "").toLowerCase() ===
            preferProductKey
          );
        })
        .map((b) => ({
          value: String(b.ImpBatchId || b.impBatchId),
          label: batchOptionLabel(b),
          row: b,
        })),
    [balances, preferProductKey],
  );

  const batchOptionsWithPlaceholder = useMemo(
    () => [{ value: "", label: "Select batch" }, ...batchOptions],
    [batchOptions],
  );

  const selectedBatch = useMemo(
    () => batchOptions.find((o) => o.value === form.impBatchId)?.row || null,
    [batchOptions, form.impBatchId],
  );

  const unitOptions = useMemo(() => {
    if (!form.impBatchId || !selectedBatch) {
      return [{ value: "", label: "Select batch first" }];
    }
    const matchedId = matchStorageUnitIdFromBatch(units, selectedBatch);
    const matched = (units || []).find(
      (u) =>
        String(u.StorageUnitId || u.storageUnitId || "") === String(matchedId),
    );
    if (!matched) {
      return [
        {
          value: "",
          label: "No storage location linked to this batch",
        },
      ];
    }
    const id = String(matched.StorageUnitId || matched.storageUnitId);
    const code = matched.UnitCode || matched.unitCode || "";
    const name = matched.UnitName || matched.unitName || "";
    const base = [code, name].filter(Boolean).join(" — ") || id;
    const tempRange = formatStorageTempRange(
      matched.MinTempC ?? matched.minTempC,
      matched.MaxTempC ?? matched.maxTempC,
      matched.TempUnit || matched.tempUnit || "C",
      "",
    );
    return [
      {
        value: id,
        label: tempRange ? `${base} [${tempRange}]` : base,
        row: matched,
      },
    ];
  }, [form.impBatchId, selectedBatch, units]);

  const rackOptions = useMemo(() => {
    if (!form.impBatchId || !selectedBatch || !form.storageUnitId) {
      return [{ value: "", label: "Select batch first" }];
    }
    const unitId = String(form.storageUnitId);
    const unitRacks = (racks || []).filter(
      (r) =>
        String(r.StorageUnitId || r.storageUnitId || "") === unitId,
    );
    if (!unitRacks.length) {
      return [{ value: "", label: "No rack for this storage location" }];
    }
    const matchedId = matchStorageRackIdFromBatch(
      unitRacks,
      selectedBatch,
      unitId,
    );
    const matched = unitRacks.find(
      (r) =>
        String(r.StorageRackId || r.storageRackId || "") === String(matchedId),
    );
    if (!matched) {
      /* Fridge has racks but batch has no rack recorded — still show linked fridge rack list locked empty. */
      return [
        {
          value: "",
          label: "No rack linked to this batch",
        },
      ];
    }
    const id = String(matched.StorageRackId || matched.storageRackId);
    const code = matched.RackCode || matched.rackCode || "";
    const name = matched.RackName || matched.rackName || "";
    return [
      {
        value: id,
        label: [code, name].filter(Boolean).join(" — ") || id,
        row: matched,
      },
    ];
  }, [form.impBatchId, form.storageUnitId, selectedBatch, racks]);

  const unitHasRacks = useMemo(() => {
    const unitId = String(form.storageUnitId || "");
    if (!unitId) return false;
    return (racks || []).some(
      (r) => String(r.StorageUnitId || r.storageUnitId || "") === unitId,
    );
  }, [form.storageUnitId, racks]);

  const dispenseStockHint = useMemo(() => {
    if (!balances || balances.length === 0) {
      return "No available stock for this study/site. Receive IMP first, then release stock before dispensing.";
    }
    const heldTotal = balances.reduce(
      (sum, b) =>
        sum + Number(b.HeldQuantity ?? b.heldQuantity ?? 0),
      0,
    );
    const availTotal = balances.reduce(
      (sum, b) =>
        sum +
        Number(
          b.AvailableOnHand ?? b.availableOnHand ?? 0,
        ),
      0,
    );
    if (availTotal <= 0 && heldTotal > 0) {
      return `${heldTotal} units are currently on hold / require review and cannot be dispensed. Open Inventory to review or release eligible stock.`;
    }
    if (availTotal <= 0) {
      return "No available stock for this IMP. Stock may be held, in transit, or not yet released.";
    }
    return "";
  }, [balances]);

  const availableKits = useMemo(
    () =>
      kits.filter((k) =>
        ["Available", "Released"].includes(String(k.Status || k.status || "")),
      ),
    [kits],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!showModal || !ctx.studyId) {
        setParticipants([]);
        setUnits([]);
        setRacks([]);
        return;
      }
      const [pRes, uRes] = await Promise.all([
        fetchStudyParticipants(ctx.studyId, ctx.siteId),
        ctx.siteId
          ? fetchStorageUnits(ctx.studyId, ctx.siteId)
          : Promise.resolve({ success: true, rows: [], racks: [] }),
      ]);
      if (cancelled) return;
      if (pRes.success) setParticipants(pRes.rows || []);
      if (uRes.success) {
        setUnits(uRes.rows || []);
        setRacks(uRes.racks || []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showModal, ctx.studyId, ctx.siteId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!showModal || !ctx.studyId || !ctx.siteId || !form.impBatchId) {
        setKits([]);
        return;
      }
      const result = await fetchImpKits(ctx.studyId, ctx.siteId, {
        impBatchId: form.impBatchId,
      });
      if (!cancelled && result.success) setKits(result.rows || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [showModal, ctx.studyId, ctx.siteId, form.impBatchId]);

  const openParticipantReturn = useCallback((row) => {
    const txnId = String(row?.ImpTxnId || row?.impTxnId || "").trim();
    if (!txnId) return;
    setReturnOverlayTxnId(txnId);
  }, []);

  const closeReturnOverlay = useCallback(() => {
    setReturnOverlayTxnId("");
  }, []);

  const onReturnOverlaySuccess = useCallback(() => {
    setReturnOverlayTxnId("");
    refresh();
  }, [refresh]);

  const openDispenseFormFill = useCallback(
    async (row) => {
      const phase = row?.FormDuePhase || "fill";
      const items = row?.FormDueItems || [];
      const workItem = items[0];
      if (!workItem) return;

      setFormFillBusy(true);
      try {
        let instanceId =
          workItem.FormInstanceId || workItem.formInstanceId || null;
        const occurrenceId =
          workItem.FormOccurrenceId || workItem.formOccurrenceId;
        const templateId =
          workItem.FormTemplateId || workItem.formTemplateId;

        if (!instanceId) {
          if (phase === "approve" || phase === "locked") {
            showAlert({
              title: "Form unavailable",
              message: "No submitted form instance is linked for review.",
              type: "info",
              buttonText: "OK",
            });
            return;
          }
          if (!templateId) {
            showAlert({
              title: "Form unavailable",
              message: "No form template is mapped to this requirement.",
              type: "info",
              buttonText: "OK",
            });
            return;
          }
          const created = await createInstance({
            FormTemplateId: templateId,
            StudyId: ctx.studyId,
            SiteId: ctx.siteId,
            Title:
              workItem.Title ||
              workItem.title ||
              workItem.TemplateName ||
              "Pharmacy form",
          });
          if (!created.success || !created.formInstanceId) {
            showAlert({
              title: "Form unavailable",
              message:
                created.message ||
                "Could not create form instance. Is e-Source licensed?",
              type: "info",
              buttonText: "OK",
            });
            return;
          }
          instanceId = created.formInstanceId;
          const linked = await linkFormInstance({
            FormOccurrenceId: occurrenceId,
            FormInstanceId: instanceId,
          });
          if (!linked.success) {
            showAlert({
              title: "Form unavailable",
              message: linked.message || "Instance created but link failed.",
              type: "info",
              buttonText: "OK",
            });
            return;
          }
          refreshFormDues();
        }

        setFormFillModal({
          formInstanceId: instanceId,
          workItem: {
            ...workItem,
            FormInstanceId: instanceId,
            LinkedTxnId:
              workItem.LinkedTxnId ||
              workItem.linkedTxnId ||
              null,
            StudyId: workItem.StudyId || ctx.studyId,
            SiteId: workItem.SiteId || ctx.siteId,
          },
          mode:
            phase === "approve"
              ? "review"
              : phase === "locked"
                ? "locked"
                : "fill",
        });
      } finally {
        setFormFillBusy(false);
      }
    },
    [ctx.studyId, ctx.siteId, refreshFormDues, showAlert],
  );

  const confirmRequestedDispense = useCallback(
    (row) => {
      const txnId = String(row?.ImpTxnId || row?.impTxnId || "").trim();
      if (!txnId || busy) return;

      showAlert({
        title: "Confirm dispense",
        message:
          "Are you sure you want to confirm this dispense request? You must sign and authenticate to complete this action.",
        type: "warning",
        buttonText: "Continue",
        cancelText: "Cancel",
        onConfirm: async () => {
          hideAlert();
          const sign = await openESignatureConfirmModal({
            title: "Sign dispense",
          });
          if (
            !sign ||
            sign.cancelled ||
            Number(sign.Success ?? sign.success ?? 0) !== 1 ||
            !sign.SignatureBase64
          ) {
            return;
          }

          const auth = await openAuthModal({
            introMessage:
              "Re-authenticate to confirm this IMP dispense. Dispense notes are required.",
            showDescription: true,
            requireDescription: true,
            descriptionLabel: "Dispense notes",
            descriptionPlaceholder: "Enter notes for this dispense…",
            descriptionMaxLength: 1000,
          });
          if (
            !auth ||
            auth.cancelled ||
            Number(auth.Success ?? auth.success ?? 0) !== 1
          ) {
            return;
          }
          const notes = String(auth.Description || auth.description || "").trim();
          if (!notes) {
            showAlert({
              title: "Dispense notes required",
              message: "Enter dispense notes to confirm this dispense.",
              type: "danger",
              buttonText: "OK",
            });
            return;
          }

          setBusy(true);
          try {
            const result = await confirmDispenseRequest({
              StudyId: ctx.studyId,
              SiteId: ctx.siteId,
              ImpTxnId: txnId,
              Notes: notes,
            });
            if (!result.success) {
              showAlert({
                title: "Confirm dispense failed",
                message: result.message || "Confirm dispense failed.",
                type: "danger",
                buttonText: "OK",
              });
              return;
            }
            await refresh();
            refreshFormDues();
            showAlert({
              title: "Dispense confirmed",
              message: "Dispense request was posted successfully.",
              type: "success",
              buttonText: "OK",
            });
          } finally {
            setBusy(false);
          }
        },
      });
    },
    [
      busy,
      ctx.studyId,
      ctx.siteId,
      hideAlert,
      refresh,
      refreshFormDues,
      showAlert,
    ],
  );

  const renderCell = useCallback(
    (row, field) => {
      if (field === "FormDue") {
        const phase = row.FormDuePhase || "none";
        if (phase === "none") {
          if (isDispenseRequestedRow(row)) {
            return (
              <span className="text-muted" title="Create request linked form">
                —
              </span>
            );
          }
          return "—";
        }
        const label =
          row.FormDueLabel ||
          (phase === "fill"
            ? "Fill form"
            : phase === "approve"
              ? "Approval pending"
              : "Approved");
        const toneClass =
          phase === "fill"
            ? "epsf-receipt-form-due--fill"
            : phase === "approve"
              ? "epsf-receipt-form-due--approve"
              : "epsf-receipt-form-due--locked";
        return (
          <button
            type="button"
            className={`epsf-receipt-form-due epsf-receipt-form-due--action ${toneClass}`}
            title={row.FormDue || label}
            disabled={formFillBusy}
            onClick={(e) => {
              e.stopPropagation();
              openDispenseFormFill(row);
            }}
          >
            {label}
          </button>
        );
      }
      if (field === "ReceiptStatus") {
        if (isDispenseRequestedRow(row)) {
          const canConfirm =
            row.FormDuePhase === "locked" || row.FormDuePhase === "none";
          return (
            <span className="d-inline-flex flex-column gap-1 align-items-start">
              {renderReceiptStatusBadge("DispenseRequested")}
              {canConfirm ? (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  disabled={busy}
                  title="Confirm dispense (final post)"
                  onClick={(e) => {
                    e.stopPropagation();
                    confirmRequestedDispense(row);
                  }}
                >
                  Confirm dispense
                </button>
              ) : null}
            </span>
          );
        }
        return renderReceiptStatusBadge("Posted");
      }
      if (field === "ReturnedQuantity") {
        if (isDispenseRequestedRow(row)) return "—";
        const returnedLabel = formatImpQtyWithTracking(
          row,
          row.ReturnedQuantity ?? row.returnedQuantity,
        );
        const remaining = Number(
          row.RemainingReturnableQuantity ??
            row.remainingReturnableQuantity ??
            0,
        );
        const canOpenReturn =
          (canReturn || canView) && Number.isFinite(remaining) && remaining > 0;
        if (!canOpenReturn) {
          return returnedLabel;
        }
        return (
          <button
            type="button"
            className="btn btn-link p-0 epsf-dispense-return-link"
            title="Open participant return"
            onClick={() => openParticipantReturn(row)}
          >
            {returnedLabel === "—" ? "Return" : returnedLabel}
          </button>
        );
      }
      return renderImpMovementCell(row, field);
    },
    [
      busy,
      canReturn,
      canView,
      confirmRequestedDispense,
      formFillBusy,
      openDispenseFormFill,
      openParticipantReturn,
    ],
  );

  const openAdd = () => {
    setForm(emptyForm());
    setKits([]);
    setClientRequestId(newClientRequestId());
    setFormError("");
    setError("");
    setFormBlocked(false);
    setFormBlockMessage("");
    setNeedsRequest(false);
    setShowModal(true);
  };

  const closeModal = () => {
    if (busy || requestBusy) return;
    setShowModal(false);
    setFormError("");
    setFormBlocked(false);
    setFormBlockMessage("");
    setNeedsRequest(false);
    if (overlay) onOverlayClose?.();
  };

  const refreshDispenseFormGate = useCallback(async () => {
    if (!ctx.studyId || !ctx.siteId) {
      setFormBlocked(false);
      setFormBlockMessage("");
      setNeedsRequest(false);
      return;
    }
    setFormGateBusy(true);
    try {
      const res = await checkFormActionBlockers(
        ctx.studyId,
        ctx.siteId,
        "FINAL_DISPENSE",
      );
      const message = formBlockMessageForAction(res, "Post dispense");
      const needReq = formBlockersNeedRequest(res);
      if (res.success && message) {
        setFormBlocked(true);
        setFormBlockMessage(message);
        setNeedsRequest(needReq);
      } else if (!res.success) {
        setFormBlocked(true);
        setFormBlockMessage(
          res.message ||
            "Could not verify dispense form requirements. Post dispense is blocked until the check succeeds.",
        );
        setNeedsRequest(false);
      } else {
        setFormBlocked(false);
        setFormBlockMessage("");
        setNeedsRequest(false);
      }
    } catch {
      setFormBlocked(true);
      setFormBlockMessage(
        "Could not verify dispense form requirements. Post dispense is blocked until the check succeeds.",
      );
      setNeedsRequest(false);
    } finally {
      setFormGateBusy(false);
    }
  }, [ctx.studyId, ctx.siteId]);

  /* Gate Post only — do not create the due form on open (Create request does that). */
  useEffect(() => {
    if (!showModal) {
      setFormBlocked(false);
      setFormBlockMessage("");
      setNeedsRequest(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      await refreshDispenseFormGate();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [showModal, refreshDispenseFormGate]);

  const onCreateRequest = async () => {
    setFormError("");
    setError("");
    if (!canDispense && !canView) {
      setFormError("You do not have permission to dispense IMP.");
      return;
    }
    if (!ctx.studyId || !ctx.siteId || !form.impBatchId) {
      setFormError("Select study, site and batch.");
      return;
    }
    if (!form.participantId) {
      setFormError("Participant is required.");
      return;
    }
    if (!form.storageUnitId) {
      setFormError("Storage location is required.");
      return;
    }
    if (unitHasRacks && !form.storageRackId) {
      setFormError("Rack is required for this fridge.");
      return;
    }
    if (!form.txnDate || !form.txnTime) {
      setFormError("Dispense date and time are required.");
      return;
    }
    if (!form.selectedKitIds.length) {
      setFormError(
        "Select one or more Available kits. Empty selection is not allowed.",
      );
      return;
    }

    const qty = form.selectedKitIds.length;
    setRequestBusy(true);
    try {
      const result = await createDispenseRequest({
        StudyId: ctx.studyId,
        SiteId: ctx.siteId,
        ImpBatchId: form.impBatchId,
        ParticipantId: form.participantId,
        StorageUnitId: form.storageUnitId,
        StorageRackId: form.storageRackId || null,
        Quantity: qty,
        TxnAt: toApiDateTimeString(form.txnDate, form.txnTime),
        ReferenceNo: form.referenceNo.trim(),
        ClientRequestId: clientRequestId,
        KitIdsJson: JSON.stringify(form.selectedKitIds),
      });
      if (!result.success) {
        setFormError(result.message || "Could not create dispense request.");
        return;
      }
      setShowModal(false);
      setForm(emptyForm());
      setClientRequestId(newClientRequestId());
      await refresh();
      refreshFormDues();
      showAlert({
        title: "Dispense request created",
        message:
          "The request is on the Dispense list. Completion-role staff were emailed. Use Fill form on that row, then Confirm dispense after approval.",
        type: "success",
        buttonText: "OK",
      });
      if (overlay) onOverlaySuccess?.();
    } finally {
      setRequestBusy(false);
    }
  };

  useEffect(() => {
    if (!overlay) return undefined;
    openAdd();
    return undefined;
    // Mount-once for overlay launch from Inventory.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay]);

  // Prefill fridge + rack from selected batch source location.
  useEffect(() => {
    if (!showModal) return;
    if (!form.impBatchId || !selectedBatch || !units.length) {
      setForm((prev) =>
        prev.storageUnitId || prev.storageRackId
          ? { ...prev, storageUnitId: "", storageRackId: "" }
          : prev,
      );
      return;
    }
    const matchedUnit = matchStorageUnitIdFromBatch(units, selectedBatch);
    const matchedRack = matchedUnit
      ? matchStorageRackIdFromBatch(racks, selectedBatch, matchedUnit)
      : "";
    setForm((prev) => {
      if (
        prev.storageUnitId === matchedUnit &&
        prev.storageRackId === matchedRack
      ) {
        return prev;
      }
      return {
        ...prev,
        storageUnitId: matchedUnit || "",
        storageRackId: matchedRack || "",
      };
    });
  }, [showModal, form.impBatchId, selectedBatch, units, racks]);

  // Auto-select preferred batch + storage once options load (overlay).
  useEffect(() => {
    if (!overlay || !showModal) return;
    if (!batchOptions.length) return;

    setForm((prev) => {
      let next = { ...prev };
      let changed = false;

      if (!next.impBatchId) {
        const preferred =
          preferBatchId &&
          batchOptions.some((o) => o.value === preferBatchId)
            ? preferBatchId
            : batchOptions[0].value;
        if (preferred) {
          next.impBatchId = preferred;
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [overlay, showModal, batchOptions, preferBatchId]);

  const toggleKit = (kitId) => {
    setForm((prev) => {
      const set = new Set(prev.selectedKitIds);
      if (set.has(kitId)) set.delete(kitId);
      else set.add(kitId);
      return {
        ...prev,
        selectedKitIds: Array.from(set),
      };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setError("");
    if (!canDispense && !canView) {
      setFormError("You do not have permission to dispense IMP.");
      return;
    }
    if (formBlocked || formGateBusy) {
      setFormError(
        formBlockMessage ||
          "Post dispense is blocked until the required form is completed and approved.",
      );
      return;
    }
    if (!ctx.studyId || !ctx.siteId || !form.impBatchId) {
      setFormError("Select study, site and batch.");
      return;
    }
    if (!form.participantId) {
      setFormError("Participant is required.");
      return;
    }
    if (!form.storageUnitId) {
      setFormError("Storage location is required.");
      return;
    }
    if (unitHasRacks && !form.storageRackId) {
      setFormError("Rack is required for this fridge.");
      return;
    }
    if (!form.txnDate || !form.txnTime) {
      setFormError("Dispense date and time are required.");
      return;
    }
    if (!form.selectedKitIds.length) {
      setFormError(
        "Select one or more Available kits. Empty selection is not allowed.",
      );
      return;
    }

    const qty = form.selectedKitIds.length;
    const countLabel = `${qty} kit${qty === 1 ? "" : "s"}`;

    showAlert({
      title: "Confirm dispense",
      message: `Are you sure you want to dispense ${countLabel}? You must sign and authenticate to complete this action.`,
      type: "warning",
      buttonText: "Continue",
      cancelText: "Cancel",
      onConfirm: async () => {
        hideAlert();
        const sign = await openESignatureConfirmModal({
          title: "Sign dispense",
        });
        if (
          !sign ||
          sign.cancelled ||
          Number(sign.Success ?? sign.success ?? 0) !== 1 ||
          !sign.SignatureBase64
        ) {
          return;
        }

        const auth = await openAuthModal({
          introMessage:
            "Re-authenticate to post this IMP dispense. Dispense notes are required.",
          showDescription: true,
          requireDescription: true,
          descriptionLabel: "Dispense notes",
          descriptionPlaceholder: "Enter notes for this dispense…",
          descriptionMaxLength: 1000,
        });
        if (
          !auth ||
          auth.cancelled ||
          Number(auth.Success ?? auth.success ?? 0) !== 1
        ) {
          return;
        }
        const notes = String(auth.Description || auth.description || "").trim();
        if (!notes) {
          setFormError("Dispense notes are required.");
          return;
        }
        await postDispenseAfterAuth(notes, qty);
      },
    });
  };

  const postDispenseAfterAuth = async (dispenseNotes, qty) => {
    setBusy(true);
    setFormError("");
    try {
      const result = await postImpDispense({
        StudyId: ctx.studyId,
        SiteId: ctx.siteId,
        ImpBatchId: form.impBatchId,
        ParticipantId: form.participantId,
        StorageUnitId: form.storageUnitId,
        StorageRackId: form.storageRackId || null,
        Quantity: qty,
        TxnAt: toApiDateTimeString(form.txnDate, form.txnTime),
        ReferenceNo: form.referenceNo.trim(),
        Notes: String(dispenseNotes || "").trim(),
        ClientRequestId: clientRequestId,
        KitIdsJson: JSON.stringify(form.selectedKitIds),
      });
      if (!result.success) {
        showAlert({
          title: "Dispense failed",
          message: result.message || "Dispense failed.",
          type: "danger",
          buttonText: "OK",
        });
        return;
      }
      setShowModal(false);
      setForm(emptyForm());
      await refresh();
      if (overlay) onOverlaySuccess?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <EpsfAccessGate allow={allow} loading={loading}>
      {!overlay ? (
      <div className="epsf-list-page">
        <EpsfPageBreadcrumb
          items={[
            { label: "ePSF", path: "/epsf/imp/inventory" },
            { label: "IMP" },
            { label: "Dispense" },
          ]}
          actions={[
            { icon: "fas fa-refresh", label: "Refresh", onClick: refresh },
            {
              icon: "fas fa-plus",
              label: "Dispense IMP",
              onClick: openAdd,
            },
            ...(canEmergencyUnblind
              ? [
                  {
                    icon: "fas fa-user-secret",
                    label: "Emergency unblind",
                    onClick: async () => {
                      const kitId = form.selectedKitIds?.[0];
                      const kit = (kits || []).find(
                        (k) => String(k.ImpKitId || k.impKitId) === String(kitId || ""),
                      );
                      const fallback = (kits || []).find(
                        (k) =>
                          String(k.Status || k.status || "").toLowerCase() ===
                          "dispensed",
                      );
                      const target = kit || fallback;
                      if (!target) {
                        setError(
                          "Select or identify a dispensed kit before emergency unblind.",
                        );
                        return;
                      }
                      const result = await runEmergencyUnblind({
                        studyId: ctx.studyId,
                        siteId: ctx.siteId,
                        participantId:
                          form.participantId ||
                          target.CurrentParticipantId ||
                          target.currentParticipantId ||
                          null,
                        impKitId: target.ImpKitId || target.impKitId,
                        kitNumber: target.KitNumber || target.kitNumber,
                      });
                      if (result.cancelled) return;
                      if (!result.success) {
                        setError(result.message || "Emergency unblind failed.");
                        return;
                      }
                      const r = result.reveal || {};
                      window.alert(
                        `${result.message}\n\nKit: ${r.kitNumber || ""}\nTreatment: ${r.treatmentType || ""}\nProduct: ${r.productName || ""} (${r.productCode || ""})`,
                      );
                    },
                  },
                ]
              : []),
          ]}
        />

        {error ? <p className="epsf-list-page__error">{error}</p> : null}

        <div className="epsf-report-wrap">
          <ReportTable
            rows={dispenseRows}
            loading={listLoading}
            fieldOptions={IMP_DISPENSE_FIELD_OPTIONS}
            defaultColumns={IMP_DISPENSE_DEFAULT_COLUMNS}
            getFieldLabel={(field) =>
              IMP_DISPENSE_FIELD_OPTIONS.find((x) => x.key === field)?.label ||
              field
            }
            renderCell={renderCell}
            getRowKey={getImpTxnRowKey}
            persistLayoutKey="epsf-imp-dispense-txns-v7"
            emptyMessage={
              !ctx.studyId || !ctx.siteId
                ? "Select study and site in the breadcrumb bar."
                : "No dispenses for this study and site yet."
            }
            pageSizeOptions={[10, 25, 50, 100]}
            columnSearchMode="client"
          />
        </div>
      </div>
      ) : null}

        {showModal ? (
          <div
            className="modal fade show d-block epsf-modal"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <form onSubmit={onSubmit}>
                  <div className="modal-header">
                    <h5 className="modal-title">Dispense IMP</h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={closeModal}
                      disabled={busy}
                    />
                  </div>
                  <div className="modal-body">
                    <div className="row g-3">
                      <div className="col-12">
                        <div className="epsf-form-field">
                          <label className="form-label">Participant <span className="mandatory">*</span></label>
                          <Dropdown
                            options={participantOptions}
                            selected={findOption(
                              participantOptions,
                              form.participantId,
                            )}
                            onSelect={(opt) =>
                              setForm((prev) => ({
                                ...prev,
                                participantId: opt?.value || "",
                              }))
                            }
                            showSearch
                          />
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="epsf-form-field">
                          <label className="form-label">Batch / Lot <span className="mandatory">*</span></label>
                          <Dropdown
                            options={batchOptionsWithPlaceholder}
                            selected={findOption(
                              batchOptionsWithPlaceholder,
                              form.impBatchId,
                            )}
                            onSelect={(opt) =>
                              setForm((prev) => ({
                                ...prev,
                                impBatchId: opt?.value || "",
                                selectedKitIds: [],
                                storageUnitId: "",
                                storageRackId: "",
                              }))
                            }
                            showSearch
                          />
                          {!batchOptions.length && dispenseStockHint ? (
                            <p className="form-text text-warning mb-0 mt-1">
                              {dispenseStockHint}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="epsf-form-field">
                          <label className="form-label">
                            Storage location <span className="mandatory">*</span>
                          </label>
                          <Dropdown
                            options={unitOptions}
                            selected={findOption(unitOptions, form.storageUnitId)}
                            onSelect={(opt) =>
                              setForm((prev) => ({
                                ...prev,
                                storageUnitId: opt?.value || "",
                                storageRackId: "",
                              }))
                            }
                            showSearch
                          />
                          <p className="form-text mb-0 mt-1">
                            Auto-filled from the batch source storage location.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="epsf-form-field">
                          <label className="form-label">
                            Rack{" "}
                            {unitHasRacks ? (
                              <span className="mandatory">*</span>
                            ) : null}
                          </label>
                          <Dropdown
                            options={rackOptions}
                            selected={findOption(
                              rackOptions,
                              form.storageRackId,
                            )}
                            onSelect={(opt) =>
                              setForm((prev) => ({
                                ...prev,
                                storageRackId: opt?.value || "",
                              }))
                            }
                            showSearch
                            disabled={!form.storageUnitId || !unitHasRacks}
                          />
                          <p className="form-text mb-0 mt-1">
                            Auto-filled from the batch source rack.
                          </p>
                        </div>
                      </div>
                      <div className="col-12">
                        <label className="form-label">
                          Kits <span className="mandatory">*</span>
                          {form.selectedKitIds.length
                            ? ` (${form.selectedKitIds.length} selected)`
                            : ""}
                        </label>
                        {form.impBatchId ? (
                          availableKits.length ? (
                            <div
                              className="border rounded p-2"
                              style={{ maxHeight: 180, overflow: "auto" }}
                            >
                              {availableKits.map((k) => {
                                const id = String(k.ImpKitId || k.impKitId);
                                return (
                                  <label key={id} className="d-block small mb-1">
                                    <input
                                      type="checkbox"
                                      className="me-2"
                                      checked={form.selectedKitIds.includes(id)}
                                      onChange={() => toggleKit(id)}
                                    />
                                    {k.KitNumber || k.kitNumber} —{" "}
                                    {k.Status || k.status}
                                  </label>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="form-text mb-0">
                              No Available kits for this batch at the site.
                            </p>
                          )
                        ) : (
                          <p className="form-text mb-0">
                            Select a batch to load Available kits.
                          </p>
                        )}
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="epsf-form-field">
                          <label className="form-label">Dispensed date <span className="mandatory">*</span></label>
                          <SegmentDatePicker
                            selected={form.txnDate}
                            onChange={(d) =>
                              setForm((prev) => ({ ...prev, txnDate: d }))
                            }
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="epsf-form-field">
                          <label className="form-label">Dispensed time <span className="mandatory">*</span></label>
                          <SegmentTimePicker
                            value={form.txnTime}
                            onChange={(t) =>
                              setForm((prev) => ({ ...prev, txnTime: t }))
                            }
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="epsf-form-field">
                          <label className="form-label">Dispensed by</label>
                          <input
                            className="form-control"
                            value={staffName || "Current user"}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="epsf-form-field">
                          <label className="form-label">Visit / Reference</label>
                          <input
                            className="form-control"
                            value={form.referenceNo}
                            onChange={(ev) =>
                              setForm((prev) => ({
                                ...prev,
                                referenceNo: ev.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                    {formBlockMessage ? (
                      <p className="epsf-list-page__error mt-2 mb-0">{formBlockMessage}</p>
                    ) : null}
                    {formError ? (
                      <p className="epsf-list-page__error mt-2 mb-0">{formError}</p>
                    ) : null}
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={closeModal}
                      disabled={busy || requestBusy}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={
                        needsRequest || formBlocked
                          ? "btn btn-primary"
                          : "btn btn-outline-primary"
                      }
                      onClick={onCreateRequest}
                      disabled={busy || requestBusy || formGateBusy}
                    >
                      {requestBusy ? "Creating..." : "Create request"}
                    </button>
                    <button
                      type="submit"
                      className={
                        needsRequest || formBlocked
                          ? "btn btn-outline-primary"
                          : "btn btn-primary"
                      }
                      disabled={busy || requestBusy || formGateBusy || formBlocked}
                    >
                      {busy
                        ? "Saving..."
                        : formGateBusy
                          ? "Checking forms..."
                          : "Post dispense"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        ) : null}

        {returnOverlayTxnId ? (
          <ImpReturns
            key={`ov-return-${returnOverlayTxnId}`}
            overlay
            preferDispenseTxnId={returnOverlayTxnId}
            onOverlayClose={closeReturnOverlay}
            onOverlaySuccess={onReturnOverlaySuccess}
          />
        ) : null}
      <InfoAlert {...alertState} onClose={hideAlert} />
      <EpsfFormFillModal
        open={Boolean(formFillModal?.formInstanceId)}
        formInstanceId={formFillModal?.formInstanceId || ""}
        workItem={formFillModal?.workItem || null}
        mode={formFillModal?.mode || "fill"}
        onClose={() => setFormFillModal(null)}
        onSubmitted={async () => {
          setFormFillModal(null);
          refreshFormDues();
          showAlert({
            title: "Form submitted",
            message:
              "Form submitted. If approval is required, status shows as Waiting for approval for the approver role.",
            type: "success",
          });
        }}
        onApproved={async (result) => {
          setFormFillModal(null);
          refreshFormDues();
          const wf = String(
            result?.rows?.[0]?.WorkflowStatus ||
              result?.WorkflowStatus ||
              "",
          ).toUpperCase();
          showAlert({
            title:
              wf === "CORRECTION_REQUIRED" ? "Form rejected" : "Form approved",
            message:
              wf === "CORRECTION_REQUIRED"
                ? "Form sent back for correction."
                : "Form approved. You can Confirm dispense on the request row.",
            type: wf === "CORRECTION_REQUIRED" ? "info" : "success",
          });
        }}
      />
    </EpsfAccessGate>
  );
}